<script lang="ts">
  import type { PwNextContentExplorerProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { onMount, tick } from 'svelte';
  import ContentExplorerItem from './components/ContentExplorerItem.svelte';
  import ContentExplorerAccordion from './components/ContentExplorerAccordion.svelte';
  import filterContent from '../PwNextPartials/helpers/filterContent.js';
  import ProgressBar from '$components/common/partials/ProgressBar.svelte';
  import { toKebabCase } from './helpers/toKebabCase';
  import { extractTextFromChildren } from './helpers/extractTextFromChildren';
  import { getTallestHeight } from './helpers/getTallestHeight';
  import layoutHelpers from '$lib/helpers/layout-helpers';

  export let entryId: PwNextContentExplorerProps['entryId'];
  export let customCssClass: PwNextContentExplorerProps['customCssClass'];
  export let sections: PwNextContentExplorerProps['sections'];
  export let variant: PwNextContentExplorerProps['variant'];
  export let timer: PwNextContentExplorerProps['timer'];
  export let duration: PwNextContentExplorerProps['duration'];
  export let accordion: PwNextContentExplorerProps['accordion'];

  const CONFIG = {
    FOOTER_BOTTOM_SPACING: 40,
    REDUCE_MOTION: false,
    ANIMATION_DURATION: 200,
    SECTION_DURATION: duration, // 5 seconds per section default
    IDLE_DURATION: 5000, // 5 seconds idle time before auto-advancing
  };

  let activeIndex = 0;
  let desktopContent: HTMLElement;
  let sidebarFooterWrapper: HTMLElement;
  const contentWrappers: (HTMLElement | null)[] = sections.map(() => null);
  const sidebarFooters: (HTMLElement | null)[] = sections.map(() => null);
  const isAnimating = false;
  let parentEl: HTMLElement;
  let sidebarEl: HTMLElement;
  let isInteracting: boolean;
  let tallestContentHeight: string = 'auto';
  let tallestFooterHeight: number = 0;
  const sectionButtons: HTMLButtonElement[] = [];
  const summaryEls: HTMLElement[] = [];
  let isInteractingTimeout: ReturnType<typeof setTimeout>;
  let openAccordionIndex: number = 0;
  let isMobile = false;

  onMount(() => {
    CONFIG.REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (CONFIG.REDUCE_MOTION) {
      CONFIG.ANIMATION_DURATION = 0;
    }

    sidebarEl.style.setProperty('--content-explorer-sidebar-top', window.getComputedStyle(parentEl).paddingTop);
    parentEl.style.setProperty('--content-explorer-animation-duration', `${CONFIG.ANIMATION_DURATION}ms`);
    parentEl.style.setProperty('--content-explorer-sidebar-footer-grid-rows', `repeat(${sections.length - 1}, 0)`);

    handleResize();

    if (window.location.search.includes('section')) {
      handleUpdateActiveIndexFromQueryParam();
    } else {
      openAccordionIndex = 0;
    }
  });

  async function handleUpdateActiveIndexFromQueryParam() {
    const params = new URLSearchParams(window.location.search);
    const sectionParam = params.get('section');
    const elementList = accordion ? summaryEls : sectionButtons;
    if (sectionParam) {
      const index = elementList.findIndex((el) => {
        const titleText = extractTextFromChildren(el);
        let kebabTitle;

        if (titleText) {
          kebabTitle = toKebabCase(titleText);
        }

        return kebabTitle === sectionParam;
      });
      if (index !== -1) {
        activeIndex = index;
        openAccordionIndex = index;
        await tick();
        const top = parentEl.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top,
          behavior: 'instant',
        });
      }
    }
  }

  function handleClick(newIndex: number) {
    if (newIndex === activeIndex) {
      return;
    }
    activeIndex = newIndex;

    handleUpdateUrl(newIndex);

    track(`Content Explorer - ${extractTextFromChildren(sectionButtons[newIndex])}`);
  }

  function handleUpdateUrl(index: number, titleText?: string) {
    let kebabTitle = '';

    if (!titleText) {
      const el = accordion ? summaryEls[index] : sectionButtons[index];
      titleText = extractTextFromChildren(el);
    }

    if (titleText) {
      kebabTitle = toKebabCase(titleText);
    }

    const params = new URLSearchParams(window.location.search);
    params.set('section', kebabTitle);

    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }

  function updateContentHeight() {
    if (isMobile) {
      return;
    }

    const validSidebarFooters = sidebarFooters.filter((el): el is HTMLElement => el !== null);

    if (accordion) {
      const validContentEls = [...sidebarEl.querySelectorAll('.ContentExplorerAccordion__content')];
      const validSummaryEls = [
        ...sidebarEl.querySelectorAll('.ContentExplorerAccordion .AccordionItem__summary'),
      ] as HTMLElement[];

      if (validContentEls.length === 0 || validSummaryEls.length === 0) {
        return;
      }

      const totalSummaryHeight = validSummaryEls.reduce((sum, el) => sum + el.offsetHeight, 0);
      const maxContentHeight = Math.max(...validContentEls.map((el) => el.scrollHeight));
      const bordersHeight = validSummaryEls.length; // border heights not being calculated for some reason

      if (totalSummaryHeight + bordersHeight + maxContentHeight > desktopContent.offsetHeight) {
        tallestContentHeight = `${totalSummaryHeight + bordersHeight + maxContentHeight}px`;
      }

      if (sidebarEl) {
        sidebarEl.style.height = `${totalSummaryHeight + bordersHeight + maxContentHeight}px`;
      }
    } else {
      const validContentWrappers = contentWrappers.filter((el): el is HTMLElement => el !== null);

      if (validContentWrappers.length === 0) {
        return;
      }

      tallestContentHeight = `${getTallestHeight(validContentWrappers)}px`;
    }

    tallestFooterHeight = getTallestHeight(validSidebarFooters);

    if (desktopContent) {
      desktopContent.style.height = tallestContentHeight;
    }

    if (sidebarFooterWrapper) {
      sidebarFooterWrapper.style.height = `${tallestFooterHeight + CONFIG.FOOTER_BOTTOM_SPACING}px`;
    }
  }

  function handleProgressBarComplete() {
    activeIndex = (activeIndex + 1) % sections.length;
  }

  function handleMouseOver() {
    isInteracting = true;
  }

  function handleMouseOut() {
    isInteracting = false;
  }

  function resetIsInteractingTimeout() {
    clearIsInteractingTimeout();
    isInteractingTimeout = setTimeout(() => {
      isInteracting = false;
    }, CONFIG.IDLE_DURATION);
  }

  function clearIsInteractingTimeout() {
    if (isInteractingTimeout) {
      clearTimeout(isInteractingTimeout);
    }
  }

  function handleMouseMove() {
    isInteracting = true;
    resetIsInteractingTimeout();
  }

  function handleAccordionOpen(event: CustomEvent) {
    const { index, titleText } = event.detail;
    openAccordionIndex = index; // handle one accordion open at a time on desktop
    activeIndex = index;
    handleUpdateUrl(index, titleText);

    if (isMobile) {
      setTimeout(() => {
        const summaryEl = summaryEls[index];
        const pos = summaryEl.getBoundingClientRect().top + window.scrollY;
        const offset = 69; // nav height

        // if the summary element is not in view, scroll to it
        if (pos < window.scrollY || pos > window.scrollY + window.innerHeight) {
          window.scrollTo({
            top: pos - offset,
            behavior: 'smooth',
          });
        }
      }, 300);
    }

    track(`Content Explorer - ${titleText}`);
  }

  function handleKeyDown() {
    isInteracting = true;
    resetIsInteractingTimeout();
  }

  function handleResize() {
    isMobile = window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.medium.minWidth;
    updateContentHeight();
  }

  // tracking
  function track(label: string = 'Event') {
    window.Sq.Tracking.track(
      {
        action: label,
      },
      { destinations: ['cdp'] }
    );
  }
</script>

<svelte:window on:resize={handleResize} />
<div
  class="PwNextContentExplorer PwNextContentExplorer-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  bind:this={parentEl}
>
  <div
    role="region"
    class="PwNextContentExplorer__wrapper"
    class:accordion
    on:mouseenter={handleMouseOver}
    on:mouseleave={handleMouseOut}
    on:mousemove={handleMouseMove}
    on:focusin={handleKeyDown}
    on:keydown={handleKeyDown}
  >
    <div class="PwNextContentExplorer__sidebar-wrapper">
      <nav
        class="PwNextContentExplorer__sidebar"
        class:accordion
        class:sticky={variant === 'textBlocks'}
        bind:this={sidebarEl}
      >
        {#if accordion}
          {#each sections as section, index}
            <!-- extract specific elements -->
            {@const elements = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'LinkComponent']}
            {@const filteredItems = filterContent(section.items, elements)}
            <ContentExplorerAccordion
              bind:summaryEl={summaryEls[index]}
              {index}
              section={{ ...section, items: filteredItems }}
              entryId={section.entryId}
              isOpen={openAccordionIndex === index}
              on:accordionOpen={handleAccordionOpen}
            />
          {/each}
        {:else}
          <ul class="PwNextContentExplorer__sidebar-list" role="tablist">
            {#each sections as section, index}
              <li class="PwNextContentExplorer__sidebar-list-item">
                <button
                  bind:this={sectionButtons[index]}
                  class="PwNextContentExplorer__title-btn"
                  class:active={index === activeIndex}
                  on:click={() => handleClick(index)}
                  disabled={isAnimating}
                  id={`button-${index}`}
                  aria-controls={`section-${index}`}
                  role="tab"
                  aria-selected={index === activeIndex}
                  tabindex="0"
                >
                  {#if section?.title}
                    <DynamicChildrenComponent children={[section?.title]} />
                  {:else}
                    <p>[section.title missing!]</p>
                  {/if}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </nav>
      {#if variant === 'image' && !accordion}
        <div class="PwNextContentExplorer__sidebar-footer-wrapper" bind:this={sidebarFooterWrapper}>
          {#each sections as section, index}
            <div
              class:timer
              class="PwNextContentExplorer__sidebar-footer"
              class:active={index === activeIndex}
              bind:this={sidebarFooters[index]}
              aria-hidden={index !== activeIndex}
            >
              {#each section.items as item}
                <!-- Extract specific elements -->
                {@const elements = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'LinkComponent']}
                <DynamicChildrenComponent children={filterContent([item], elements)} />
              {/each}
            </div>
          {/each}
          {#if timer}
            <ProgressBar
              isPlaying={isInteracting}
              duration={CONFIG.SECTION_DURATION}
              {activeIndex}
              onComplete={handleProgressBarComplete}
              {parentEl}
            />
          {/if}
        </div>
      {/if}
    </div>
    <div
      class="PwNextContentExplorer__desktop-content"
      class:textBlocks={variant === 'textBlocks'}
      class:image={variant === 'image' && !accordion}
      class:accordion
      bind:this={desktopContent}
    >
      {#if variant === 'textBlocks'}
        {#each sections as section, index}
          <div
            style="--content-explorer-variant-columns: 2;"
            class="PwNextContentExplorer__desktop-content-wrapper PwNextContentExplorer__desktop-content-wrapper--textBlocks"
            class:active={index === activeIndex}
            bind:this={contentWrappers[index]}
            id={`section-${index}`}
            aria-labelledby={`button-${index}`}
            role="tabpanel"
            aria-hidden={index !== activeIndex}
          >
            {#each section.items as item}
              <ContentExplorerItem {item} entryId={item.props.entryId} />
            {/each}
          </div>
        {/each}
      {:else}
        {#each sections as section, index}
          <div
            style="--content-explorer-variant-columns: 1;"
            class="PwNextContentExplorer__desktop-content-wrapper PwNextContentExplorer__desktop-content-wrapper--image"
            class:active={index === activeIndex}
            bind:this={contentWrappers[index]}
          >
            {#each section.items as item}
              <DynamicChildrenComponent children={filterContent([item], 'img')} />
            {/each}
          </div>
        {/each}
      {/if}
    </div>
    <div class="PwNextContentExplorer__mobile-content">
      {#each sections as section, index}
        <ContentExplorerAccordion
          isOpen={openAccordionIndex === index}
          bind:summaryEl={summaryEls[index]}
          {index}
          {section}
          entryId={section.entryId}
          on:accordionOpen={handleAccordionOpen}
        />
      {/each}
    </div>
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;

  .PwNextContentExplorer {
    display: flex;
    justify-content: center;
  }

  .PwNextContentExplorer__wrapper {
    display: grid;
    width: 100%;
    max-width: var(--container-width-current);
    position: relative;
    grid-template-columns: 1fr;

    @include mediaQueries.at('medium') {
      grid-template-columns: repeat(12, 1fr);
    }

    &.accordion {
      @include mediaQueries.at('medium') {
        align-items: center;
      }
    }
  }

  .PwNextContentExplorer__title-btn {
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity var(--content-explorer-animation-duration) var(--easing-standard);
    margin-bottom: 0 !important;

    &.active,
    &:hover {
      opacity: 1;
    }

    &:not(.active) {
      &:hover {
        opacity: 0.65;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .PwNextContentExplorer__desktop-content {
      transition: none;
    }
  }

  .PwNextContentExplorer__desktop-content {
    display: none;
    width: 100%;
    transition: height var(--content-explorer-animation-duration) var(--easing-standard);
    position: relative;
    grid-column: 6 / span 7;

    &.textBlocks {
      @include mediaQueries.at('medium') {
        display: grid;
        grid-column: 6 / span 8;
      }
    }

    &.accordion {
      @include mediaQueries.at('medium') {
        display: grid;
        grid-template-rows: unset;
      }
    }

    &.image {
      @include mediaQueries.at('medium') {
        display: grid;
        grid-template-rows: var(--content-explorer-sidebar-footer-grid-rows);

        .PwNextContentExplorer__desktop-content-wrapper {
          position: relative;
        }
      }
    }
  }

  .PwNextContentExplorer__desktop-content-wrapper {
    display: none;
    grid-template-columns: repeat(var(--content-explorer-variant-columns), 1fr);
    grid-template-rows: min-content;
    width: 100%;
    pointer-events: none;
    grid-area: 1 / 1;
    height: fit-content;

    &.active {
      pointer-events: auto;
    }

    @include mediaQueries.at('medium') {
      display: grid;
      gap: var(--space-40);
    }

    @include mediaQueries.at('large') {
      gap: var(--space-60);
    }
  }

  .PwNextContentExplorer__desktop-content-wrapper--image {
    opacity: 0;
    transition-delay: 0s;
    transition: opacity var(--content-explorer-animation-duration) var(--easing-standard);

    &.active {
      opacity: 1;
    }
  }

  .PwNextContentExplorer__desktop-content-wrapper--textBlocks {
    opacity: 0;
    transform: translateY(20px);
    transition-delay: 0s;
    pointer-events: none;
    transition:
      opacity var(--content-explorer-animation-duration) var(--easing-standard),
      transform var(--content-explorer-animation-duration) var(--easing-standard),
      visibility 0s linear var(--content-explorer-animation-duration);
    visibility: hidden;

    &.active {
      opacity: 1;
      transform: translateY(0);
      transition-delay: var(--content-explorer-animation-duration);
      pointer-events: all;
      visibility: visible;
    }
  }

  .PwNextContentExplorer__sidebar-wrapper {
    display: none;

    @include mediaQueries.at('medium') {
      grid-column: 1 / span 4;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: var(--space-140);
    }
  }

  .PwNextContentExplorer__sidebar {
    width: 100%;
    position: relative;
    height: fit-content;
    display: none;

    &.sticky {
      position: sticky;
      top: var(--content-explorer-sidebar-top);
    }

    @include mediaQueries.at('medium') {
      display: block;
    }

    :global {
      .ContentExplorerAccordion {
        &:first-of-type,
        .ContentExplorerItem {
          border-top: none;
        }

        &:last-of-type {
          border-bottom: none;
        }

        .AproposImageComponent {
          display: block;

          @include mediaQueries.at('medium') {
            display: none;
          }
        }
      }
    }
  }

  .PwNextContentExplorer__sidebar-footer-wrapper {
    display: none;
    width: 100%;
    position: relative;
    padding-bottom: var(--space-40);

    @include mediaQueries.at('medium') {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .PwNextContentExplorer__sidebar-footer {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 40px;
    width: 100%;
    height: fit-content;
    opacity: 0;
    transition: opacity var(--content-explorer-animation-duration) var(--easing-standard);

    &.active {
      opacity: 1;
      pointer-events: all;
      visibility: visible;
    }

    &:not(.active) {
      opacity: 0;
      pointer-events: none;
      visibility: hidden;
      transition-delay: 0s;
    }

    &[aria-hidden='true'] {
      pointer-events: none;
    }

    &[aria-hidden='false'] {
      pointer-events: all;
    }
  }

  .PwNextContentExplorer__sidebar-list {
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--space-20);
  }

  .PwNextContentExplorer__sidebar-list-item {
    padding: 0;
    list-style: none;
  }

  .PwNextContentExplorer__mobile-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    @include mediaQueries.at('medium') {
      display: none;
    }
  }
</style>
